import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClients } from '@/api/client';
import type { components as AlertComponents } from '@/api/types/alert-service';

// Per-screen query layer for S4 Alert Detail. Reads come from
// alert-service:
//   GET  /v1/alerts/{id}              — base alert record
//   GET  /v1/alerts/{id}/explanation  — Phase 3 (4/n) derived view
// Mutations:
//   POST /v1/alerts/{id}/acknowledge  — exists
//   POST /v1/alerts/{id}/snooze       — Phase 3 (4/n)
//   POST /v1/alerts/{id}/resolve      — exists

export type AlertDetail = AlertComponents['schemas']['AlertDetailResponse'];
export type AlertExplanation = AlertComponents['schemas']['AlertExplanation'];
export type EvidenceEntry = AlertComponents['schemas']['EvidenceEntry'];

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || !data) {
    const msg = (error as { error?: string } | undefined)?.error ?? `${what} failed`;
    throw new Error(msg);
  }
  return data;
}

export function useAlertDetail(clients: ApiClients, alertId: string | undefined) {
  return useQuery({
    enabled: !!alertId,
    queryKey: ['alert', alertId, 'detail'],
    queryFn: async (): Promise<AlertDetail> => {
      const { data, error } = await clients.alert.GET('/v1/alerts/{alertId}', {
        params: { path: { alertId: alertId! } },
      });
      const raw = unwrap(data, error, 'alert detail') as Record<string, unknown>;
      // The gateway returns the alert fields nested under `summary` with a few
      // renamed siblings (evidenceItems vs evidence). The screen expects a flat
      // record, so normalize here — one place — instead of threading optional
      // chaining through every field access.
      const summary = raw?.summary;
      if (summary && typeof summary === 'object') {
        return {
          ...(summary as Record<string, unknown>),
          recommendations: raw.recommendations ?? [],
          evidence: raw.evidenceItems ?? raw.evidence ?? [],
          narrative: raw.narrative,
          snoozed_until: raw.snoozed_until,
        } as unknown as AlertDetail;
      }
      return raw as unknown as AlertDetail;
    },
    staleTime: 30_000,
  });
}

export function useAlertExplanation(clients: ApiClients, alertId: string | undefined) {
  return useQuery({
    enabled: !!alertId,
    queryKey: ['alert', alertId, 'explanation'],
    queryFn: async (): Promise<AlertExplanation> => {
      const { data, error } = await clients.alert.GET(
        '/v1/alerts/{alertId}/explanation',
        { params: { path: { alertId: alertId! } } },
      );
      return unwrap(data, error, 'explanation');
    },
    staleTime: 30_000,
  });
}

// makeIdem returns a uuid-like string. The backend only requires the
// header is present and stable across a retry; uuid quality isn't
// material in dev. Real impl: crypto.randomUUID() (modern browsers).
function makeIdem(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useAcknowledgeAlert(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { alertId: string; actor: string; note?: string }) => {
      const { data, error } = await clients.alert.POST(
        '/v1/alerts/{alertId}/acknowledge',
        {
          params: {
            path: { alertId: vars.alertId },
            header: { 'Idempotency-Key': makeIdem() },
          },
          body: { acknowledgedBy: vars.actor, note: vars.note },
        },
      );
      if (error) throw new Error('acknowledge failed');
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['alert', vars.alertId] });
    },
  });
}

export function useSnoozeAlert(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { alertId: string; duration: '24h' | '7d' }) => {
      const { data, error } = await clients.alert.POST(
        '/v1/alerts/{alertId}/snooze',
        {
          params: {
            path: { alertId: vars.alertId },
            header: { 'Idempotency-Key': makeIdem() },
          },
          body: { duration: vars.duration },
        },
      );
      if (error) throw new Error('snooze failed');
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['alert', vars.alertId] });
    },
  });
}

export function useResolveAlert(clients: ApiClients) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { alertId: string; actor: string; reason: string }) => {
      const { data, error } = await clients.alert.POST(
        '/v1/alerts/{alertId}/resolve',
        {
          params: {
            path: { alertId: vars.alertId },
            header: { 'Idempotency-Key': makeIdem() },
          },
          body: { resolvedBy: vars.actor, resolutionCode: vars.reason },
        },
      );
      if (error) throw new Error('resolve failed');
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['alert', vars.alertId] });
    },
  });
}
